import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Send, Loader2, Wand2, FileText, ExternalLink, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-blue-100 text-blue-700',
  published: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function ContentDistributionPage() {
  const { selectedProjectId } = useProject();
  const socialTask = useTaskState('social-generate');
  const [searchParams] = useSearchParams();
  const [distributions, setDistributions] = useState([]);
  const [stats, setStats] = useState(null);
  const loading = socialTask.running;
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  const [oppId, setOppId] = useState(searchParams.get('opp') || '');
  const [clientWebsite, setClientWebsite] = useState('');
  const [clientIndustry, setClientIndustry] = useState('');
  const [wordCount, setWordCount] = useState(800);
  const [tone, setTone] = useState('professional');

  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [distRes, statsRes, oppRes] = await Promise.all([
        api.get('/content-distribution'),
        api.get('/content-distribution/stats'),
        api.get('/backlinks/opportunities?page=1&page_size=50'),
      ]);
      setDistributions(distRes.data.items || distRes.data.distributions || []);
      setStats(statsRes.data.stats);
      const oppItems = oppRes.data?.items || (Array.isArray(oppRes.data) ? oppRes.data : []);
      setOpportunities(oppItems.filter(o =>
        ['prospected', 'contacted', 'negotiating', 'acquired'].includes(o.status)
      ));
    } catch { /* ignore */ }
  };

  const handleGenerate = async () => {
    if (!oppId || !clientWebsite.trim()) return toast.error('Select an opportunity and enter your website');
    socialTask.start();
    try {
      const res = await api.post('/content-distribution/guest-post', {
        opportunity_id: oppId, client_website: clientWebsite,
        client_industry: clientIndustry, word_count: wordCount, tone,
        project_id: selectedProjectId || undefined,
      });
      setSelected(res.data.distribution);
      toast.success('Guest post generated!');
      fetchAll();
      socialTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
      socialTask.fail(err);
    }
  };

  const handleVariants = async (distId) => {
    socialTask.start();
    try {
      const res = await api.post(`/content-distribution/${distId}/variants`, { variant_count: 3 });
      toast.success(`${res.data.variants.length} variants generated!`);
      const updated = distributions.map(d =>
        d.distribution_id === distId ? { ...d, variants: res.data.variants } : d
      );
      setDistributions(updated);
      if (selected?.distribution_id === distId) {
        setSelected({ ...selected, variants: res.data.variants });
      }
      socialTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Variant generation failed');
      socialTask.fail(err);
    }
  };

  const updateStatus = async (distId, status) => {
    try {
      await api.put(`/content-distribution/${distId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchAll();
    } catch { toast.error('Update failed'); }
  };

  const copyArticle = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div className="space-y-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Content Distribution</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Generate guest posts for third-party sites from backlink opportunities</p>
        </div>
        {stats && (
          <div className="flex gap-2">
            <Badge variant="outline">{stats.total} articles</Badge>
            {Object.entries(stats.by_status || {}).map(([s, c]) => (
              <Badge key={s} className={STATUS_COLORS[s] || 'bg-slate-100'}>{s}: {c}</Badge>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle className="text-base">Create Guest Post</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Backlink Opportunity</label>
              <select value={oppId} onChange={e => setOppId(e.target.value)}
                className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                <option value="">Select opportunity...</option>
                {opportunities.map(o => (
                  <option key={o.opportunity_id} value={o.opportunity_id}>
                    {o.domain} — {o.opportunity_type} ({o.status})
                  </option>
                ))}
              </select>
            </div>
            <Input placeholder="Your website URL" value={clientWebsite} onChange={e => setClientWebsite(e.target.value)} />
            <Input placeholder="Your industry" value={clientIndustry} onChange={e => setClientIndustry(e.target.value)} />
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Words</label>
                <Input type="number" min={300} max={3000} value={wordCount} onChange={e => setWordCount(Number(e.target.value))} />
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500">Tone</label>
                <select value={tone} onChange={e => setTone(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                  {['professional', 'casual', 'authoritative', 'friendly'].map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleGenerate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
              Generate Guest Post
            </Button>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {selected ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{selected.title}</CardTitle>
                  <Badge className={STATUS_COLORS[selected.status] || 'bg-slate-100'}>{selected.status}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2 flex-wrap text-xs text-slate-500">
                  <span>Target: <strong>{selected.target_domain}</strong></span>
                  <span>Words: {selected.word_count}</span>
                  <span>Read time: {selected.estimated_read_time}</span>
                </div>
                <p className="text-xs text-slate-500 italic">{selected.meta_description}</p>
                <div className="flex gap-1 flex-wrap">
                  {(selected.target_keywords || []).map((kw, i) => <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>)}
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4 text-sm max-h-96 overflow-y-auto prose prose-sm dark:prose-invert max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2">
                  <ReactMarkdown>{selected.article_body}</ReactMarkdown>
                </div>
                {selected.internal_link_anchors?.length > 0 && (
                  <div className="text-xs text-slate-500">
                    <strong>Backlinks:</strong>{' '}
                    {selected.internal_link_anchors.map((a, i) => (
                      <span key={i}>"{a.anchor}" → {a.url}{i < selected.internal_link_anchors.length - 1 ? ' · ' : ''}</span>
                    ))}
                  </div>
                )}
                <div className="bg-violet-50 dark:bg-violet-950/30 rounded-lg p-3 text-xs">
                  <strong>Author Bio:</strong> {selected.author_bio}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => copyArticle(selected.article_body)}>
                    {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                    {copied ? 'Copied' : 'Copy Article'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleVariants(selected.distribution_id)} disabled={loading}>
                    <Wand2 className="h-3 w-3 mr-1" /> Variants
                  </Button>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => updateStatus(selected.distribution_id, 'submitted')}>
                    <Send className="h-3 w-3 mr-1" /> Mark Submitted
                  </Button>
                  <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(selected.distribution_id, 'published')}>
                    Mark Published
                  </Button>
                </div>
                {selected.variants?.length > 0 && (
                  <div className="space-y-2 pt-2">
                    <h4 className="text-sm font-semibold">Title/Intro Variants</h4>
                    {selected.variants.map((v, i) => (
                      <div key={i} className="border rounded-lg p-3 border-slate-200 dark:border-slate-700">
                        <p className="font-medium text-sm">{v.title}</p>
                        <p className="text-xs text-slate-500 mt-1">{v.introduction?.substring(0, 200)}...</p>
                        <Badge variant="secondary" className="text-xs mt-1">{v.angle}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-400 text-sm">
              Select a backlink opportunity and generate a guest post
            </CardContent></Card>
          )}
        </div>
      </div>

      {distributions.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">All Distributions</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {distributions.map((d, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900 transition"
                onClick={() => setSelected(d)}>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm truncate">{d.title}</h4>
                  <p className="text-xs text-slate-400">{d.target_domain} · {d.word_count} words</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[d.status] || 'bg-slate-100'}>{d.status}</Badge>
                  <span className="text-xs text-slate-400">{new Date(d.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
